import React, { useCallback, useEffect } from 'react'
import { Box, CircularProgress, Divider, Grid, Typography } from '@mui/material'
import GenericAutocomplete from '../../../../components/FormElements/FormAutoComplete'
import MultiSelectWithCheckBox from '../../../../components/FormElements/MultiSelectWithCheckBox'
import GenericButton from '../../../../components/GenericButton'
import AddIcon from '@mui/icons-material/Add'
import { ButtonSize, ButtonType } from '../../../../components/GenericButton/styles'
import { type IAttribute } from '../../../../models/Attributes'
import { ReactComponent as DeleteIcon } from '../../../../assets/trash.svg'
import { grey, shade } from '../../../../colors'
import { useQuery } from '@tanstack/react-query'
import api from '../../../../utils/api'
import { useFieldArray, useFormContext } from 'react-hook-form'
import { type IAttribute as TemplateAttributesType } from '../../../../store/slices/SurveyTemplateSlice'
import CustomTooltip from '../../../../components/CustomTooltip'

const operations = [
  { title: 'Is Not', value: 'is not' },
  { title: 'Is', value: 'is' }
]

interface AttributesFilterForGroupSettingProps {
  attributes?: TemplateAttributesType[]
  initialLabel?: string
  onAddFilterGroup?: () => void
  actionButtonLabel?: string
  onCancelFilterGroup?: () => void
  disableDeleteLast?: boolean
}

const AttributesFilterForGroupSetting: React.FC<AttributesFilterForGroupSettingProps> = ({
  attributes,
  initialLabel = 'Display If',
  onAddFilterGroup,
  actionButtonLabel = 'Add Condition Group',
  onCancelFilterGroup,
  disableDeleteLast = false
}) => {
  const { data: attributesList, isLoading, isFetching, isError } = useQuery<IAttribute[]>({
    queryKey: ['attributes', 'Course Attributes'],
    queryFn: async (): Promise<IAttribute[]> => {
      const result = await api.get('/api/attributes/', { params: { pageSize: 1000, attribute_type: 'Course Attributes' } })
      return result.data
    },
    enabled: true,
    staleTime: 10000
  })

  const { setValue, control, watch } = useFormContext()
  const { fields, append, remove } = useFieldArray({ control, name: 'courseFilters' })

  const handleAddFilter = useCallback(() => {
    append({ key: null, op: '', value: [] })
  }, [append])

  const handleKeyChange = useCallback((index: number, newValue: IAttribute | null) => {
    if (!attributesList) {
      return
    }

    setValue(`courseFilters[${index}].key`, newValue)
    setValue(`courseFilters[${index}].value`, [])
  }, [setValue, attributesList])

  const handleRemoveFilter = useCallback((index: number) => { remove(index) }, [remove])

  useEffect(() => {
    if (attributes && attributes.length > 0 && attributesList) {
      const defaultFilters = attributes.map((attr) => {
        const fullAttribute = attributesList.find(
          (a) => a.attributeKey === attr.key
        )

        return {
          key: fullAttribute ?? null,
          op: attr.op ?? '',
          value: attr.values ?? []
        }
      })
      setValue('courseFilters', defaultFilters)
    }
  }, [attributes, attributesList, setValue, isFetching, isLoading])

  const filterRow = (item: Partial<TemplateAttributesType>, index: number): JSX.Element => {
    return (
      <Grid key={item.id} container justifyContent='center'>
        {index !== 0 && (
          <Grid item width='90%' alignItems='center' justifyContent='center' mt={-4} mb={4}>
            <Divider orientation='horizontal'>and</Divider>
          </Grid>
        )}
        <Grid container justifyContent='space-between' alignItems='center' mx='5%'>
          <Grid item width='30%'>
            <GenericAutocomplete
              control={control}
              options={attributesList}
              loading={isFetching || isLoading}
              placeholder="Attribute"
              name={`courseFilters[${index}].key`}
              getOptionLabel={(option: IAttribute) => option?.attributeKey}
              getOptionSubLabel={(option: IAttribute) => (option.attributeValues ?? []).slice(0, 3).join(', ') + (option.attributeValues?.length > 3 ? '...' : '')}
              getOptionValue={(option: IAttribute) => option}
              onChange={(newValue) => { handleKeyChange(index, newValue) }}
              textFieldStyles={{ backgroundColor: grey[0] }}
              isOptionEqualToValue={(option, value) => option?.attributeKey === value?.attributeKey}
            />
          </Grid>
          <Grid item width='20%'>
            <GenericAutocomplete
              control={control}
              options={operations}
              placeholder="is"
              name={`courseFilters[${index}].op`}
              getOptionLabel={(option) => option?.title}
              getOptionValue={(option) => option?.value}
              onChange={(newValue) => { setValue(`courseFilters[${index}].op`, newValue?.value ?? '') }}
              textFieldStyles={{ backgroundColor: grey[0] }}
            />
          </Grid>
          <Grid item width='40%'>
            <MultiSelectWithCheckBox
              name={`courseFilters[${index}].value`}
              options={watch(`courseFilters[${index}].key`)?.attributeValues ?? []}
              placeholder="Values"
              getOptionLabel={(option: string) => option}
              getOptionValue={(option: string) => option}
              loading={isFetching || isLoading}
              allowMultiInput
              textFieldStyles={{ backgroundColor: grey[0] }}
            />
          </Grid>
          <Grid item mt={-6} width='5%'>
            <CustomTooltip title={disableDeleteLast && fields.length === 1 ? 'At least one condition is required when editing a group.' : ''}>
              <GenericButton
                icon={<DeleteIcon opacity={disableDeleteLast && fields.length === 1 ? 0.5 : 1} />}
                buttonSize={ButtonSize.Medium}
                buttonType={ButtonType.Ghost}
                style={{ background: 'transparent' }}
                onClick={() => { handleRemoveFilter(index) }}
                disabled={disableDeleteLast && fields.length === 1}
              />
            </CustomTooltip>
          </Grid>
        </Grid>
      </Grid>
    )
  }

  const renderContent = (): JSX.Element => {
    if (isFetching || isLoading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={'100px'}>
          <CircularProgress />
        </Box>
      )
    }

    if (isError) {
      return (
        <Typography color="error" align="center">
          Failed to load attributes
        </Typography>
      )
    }

    return (
      <Box display="flex" flexDirection="column" gap={2}>
        <Typography fontWeight={600} textAlign="left" mb={2}>{fields.length > 0 && initialLabel}</Typography>

        {/* Condition rows */}
        {fields.map((item, index) => filterRow(item, index))}

        {/* Display a human-readable description of the filter */}
        {fields.length > 0 && (
          <Typography textAlign='left' fontSize='14px'>
            {fields.map((item, index) => {
              const values = watch(`courseFilters[${index}].value`) ?? []
              let valuesDisplay = '(insert values)'
              const attributeKey = String(watch(`courseFilters[${index}].key`)?.attributeKey ?? '(insert attribute)')
              const operator = String(watch(`courseFilters[${index}].op`) || '(insert operator)')

              if (values?.length) {
                if (values.length === 1) valuesDisplay = values[0]
                else valuesDisplay = `${values.slice(0, -1).join(', ')} or ${values.at(-1)}`
              }

              return (
                <React.Fragment key={index}>
                  {
                    index === 0
                      ? 'This template section appears for course sections where the '
                      : index === fields.length - 1
                        ? ', and the '
                        : ', the '
                  }
                  <Typography fontWeight={600} display="inline" fontSize='14px' component='span'>
                    {attributeKey}
                  </Typography>
                  {` ${operator} `}
                  <Typography fontStyle='italic' display="inline" fontSize='14px' component='span'>
                    {valuesDisplay}{index === fields.length - 1 ? '.' : ''}
                  </Typography>
                </React.Fragment>
              )
            })}
          </Typography>
        )}

        {/* Add new condition button */}
        <Box width="100%" display="flex" justifyContent={fields.length === 0 ? 'center' : 'flex-end'}>
          <GenericButton
            label="Condition"
            buttonSize={ButtonSize.Medium}
            buttonType={fields.length === 0 ? ButtonType.Secondary : ButtonType.Ghost}
            startIcon={<AddIcon sx={{ mr: -2 }} />}
            onClick={handleAddFilter}
            sx={{ background: 'transparent', justifyContent: 'center', '&:hover': { opacity: 0.8 } }}
          />
        </Box>

        {/* Action buttons for the filter group */}
        {onAddFilterGroup && fields.length > 0 && (
          <Box width="100%" display="flex" justifyContent="flex-end" gap={2}>
            {onCancelFilterGroup && (
            <CustomTooltip title=''>
              <GenericButton
                label="Cancel"
                buttonSize={ButtonSize.Medium}
                buttonType={ButtonType.Secondary}
                onClick={onCancelFilterGroup}
              />
            </CustomTooltip>
            )}
            <GenericButton
              label={actionButtonLabel}
              buttonSize={ButtonSize.Medium}
              buttonType={ButtonType.Primary}
              onClick={onAddFilterGroup}
            />
          </Box>
        )}
      </Box>
    )
  }

  return (
    <Box>
      <Typography color='#000' fontWeight={'bold'} mb={5} mt={4}>Display Condition</Typography>

      <Box
        mt={4}
        display="flex"
        flexDirection="column"
        justifyContent="center"
        border={`1px solid ${shade[650]}`}
        padding="24px 16px"
        borderRadius="8px"
        bgcolor={shade[750]}
      >
        {renderContent()}
      </Box>
    </Box>

  )
}

export default AttributesFilterForGroupSetting
